/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ICittaJournalCompetitionFormData } from './citta-journal-competition-form.data';
import { ICittaJournalCompetitionFormProps } from './citta-journal-competition-form.props.autogenerated';

interface ICittaJournalCompetitionFormFormState {
    firstName: string;
    lastName: string;
    emailAddress: string;
    country: string;
    termsAndContitionsChecked: boolean;
    privacyPolicyChecked: boolean;
    eAValError: boolean;
    eAError: boolean;
    fNError: boolean;
    lNError: boolean;
    tsCsError: boolean;
    rCCError: boolean;
    reqError: boolean;
    responseReceived: boolean;
    responseError: boolean;
    responseSuccess: boolean;
    showResponseSuccess: boolean;
    reCaptchaChecked: boolean;
    charactersLeft?: number;
    firstNameErrorClass: string;
    lastNameErrorClass: string;
    emailErrorClass: string;
    tsCsErrorClass: string;
}

export interface ICittaJournalCompetitionFormForm {
    FormWrapper: INodeProps;
    FormContainer: INodeProps;
    firstAndLastNameInput: React.ReactNode;
    emailInput: React.ReactNode;
    countryInput: React.ReactNode;
    checkboxTermsAndConditions: React.ReactNode;
    checkboxPrivacyPolicy: React.ReactNode;
    reCaptcha: React.ReactNode;
    subscribeButtton: React.ReactNode;
    subscribeThanks: React.ReactNode;
}


export interface ICittaJournalCompetitionFormViewProps extends ICittaJournalCompetitionFormProps<ICittaJournalCompetitionFormData> {
    SubscribeWrapper: IModuleProps;
    subscribeResponse: React.ReactNode;
    subscribeForm: ICittaJournalCompetitionFormForm;
    responseSuccess: boolean;
    showResponseSuccess: boolean;
    formErrors: React.ReactNode;
    formErrorsNodeProps: INodeProps;
    successResponse: React.ReactNode;
    showSuccessResponse: boolean;
    successResponseNodeProps: INodeProps;
}

/**
 *
 * CittaCompetitionForm component
 * @extends {React.PureComponent<ICittaCompetitionFormProps<ICittaCompetitionFormData>>}
 */
class CittaCompetitionForm extends React.PureComponent<ICittaJournalCompetitionFormProps<ICittaJournalCompetitionFormData>, ICittaJournalCompetitionFormFormState> {
    private _FirstNameInputRef: React.RefObject<HTMLInputElement>;
    private _LastNameInputRef: React.RefObject<HTMLInputElement>;
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _TermsAndContitionsRef: React.RefObject<HTMLInputElement>;
    private _PrivacyPolicyRef: React.RefObject<HTMLInputElement>;
    private _recaptchaRef: React.RefObject<ReCAPTCHA>;

    private firstNameValue: boolean = false;
    private lastNameValue: boolean = false;
    private emailAddressValue: boolean = false;
    private formInputValues: boolean = false;
    private responseMessage: string | undefined;
    private moduleClassName: string = 'newsletterSubWrap';
    private recaptchaErrorMessage: string | undefined;

    constructor(props: ICittaJournalCompetitionFormProps<ICittaJournalCompetitionFormData>) {
        super(props);
        this._FirstNameInputRef = React.createRef();
        this._LastNameInputRef = React.createRef();
        this._EmailAddressInputRef = React.createRef();
        this._TermsAndContitionsRef = React.createRef();
        this._PrivacyPolicyRef = React.createRef();
        this._recaptchaRef = React.createRef();
        this._handleSubmit = this._handleSubmit.bind(this);

        this.state = {
            firstName: '',
            lastName: '',
            emailAddress: '',
            country: 'NZ',
            termsAndContitionsChecked: false,
            privacyPolicyChecked: false,
            eAValError: false,
            fNError: false,
            lNError: false,
            eAError: false,
            tsCsError: false,
            rCCError: false,
            reqError: false,
            responseReceived: false,
            responseError: false,
            reCaptchaChecked: false,
            responseSuccess: false,
            showResponseSuccess: false,
            charactersLeft: Number(this.props.resources.messageCharactersLimit) || 140,
            firstNameErrorClass: 'hide',
            lastNameErrorClass: 'hide',
            emailErrorClass: 'hide',
            tsCsErrorClass: 'hide'
        };
    }

    public render(): JSX.Element | null {
        const viewProps: ICittaJournalCompetitionFormViewProps = {
            ...(this.props as ICittaJournalCompetitionFormProps<ICittaJournalCompetitionFormData>),
            SubscribeWrapper: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, this.props.config.className)
            },
            subscribeResponse: this._getSubmitResponse(),
            subscribeForm: this._renderForm(),
            responseSuccess: this.state.responseSuccess,
            showResponseSuccess: this.state.showResponseSuccess,
            formErrors: this._renderErrors(),
            formErrorsNodeProps: {
                className: 'competition-form-errors'
            },
            successResponse: this._renderSuccessResponse(),
            showSuccessResponse: this.state.responseSuccess,
            successResponseNodeProps: {
                className: 'competition-form-success'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getSubmitResponse(): React.ReactNode {
        return (
            this.state.responseReceived && this.responseMessage !== '' || undefined
                ? (
                    <div className={`response-message ${this.state.responseError ? 'response-error' : ''}`}>
                        <p className='response-message-content'>{this.responseMessage}</p>
                    </div>
                )
                : null
        );
    }
    
    private _renderSuccessResponse(): React.ReactNode {
        return (
            this.state.responseSuccess
                ? (
                    <div className="response-succcess">
                        <h2>{this.props.config.successTextResponse}</h2>
                    </div>
                )
                : null
        );
    }


    private _renderForm(): ICittaJournalCompetitionFormForm {
        return {
            FormWrapper: {
                className: 'competition-form',
            },
            FormContainer: {
                className: 'competition-form-div',
            },
            firstAndLastNameInput: this._renderFirstAndLastNameInput(),
            emailInput: this._renderEmailInput(),
            countryInput: this._renderCountryInput(),
            checkboxTermsAndConditions: this._renderCheckboxTermsAndConditions(),
            checkboxPrivacyPolicy: this._renderCheckboxPrivacyPolicy(),
            reCaptcha: this._renderReCaptcha(),
            subscribeButtton: this._renderSubmitButton(),
            subscribeThanks: this._renderSubmitThanks()
        };
    }

    private _renderFirstAndLastNameInput(): React.ReactNode {
        return (
            <div className='name-input-container'>
                <div className='name-input'>
                    <input
                        ref={this._FirstNameInputRef}
                        type='text'
                        onChange={this._handleChange('firstName')}
                        aria-label='First Name'
                        className='first-name-input-text'
                        placeholder={this.props.config.firstNamePlaceholderText}
                        value={this.state.firstName}
                        required
                    />
                </div>
                <div className='name-input'>
                    <input
                        ref={this._LastNameInputRef}
                        type='text'
                        onChange={this._handleChange('lastName')}
                        aria-label='First Name'
                        className='name-input-text'
                        placeholder={this.props.config.lastNamePlaceholderText}
                        value={this.state.lastName}
                        required
                    />
                </div>
            </div>
        );
    }
    private _renderEmailInput(): React.ReactNode {
        return (
            <div className='email-address-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('email-address-input') : ''}
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this._handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    value={this.state.emailAddress}
                    required
                    placeholder={this.props.config.emailInputPlaceholder}
                />
            </div>
        );
    }

    private _renderCountryInput() : React.ReactNode {
        return (
            <div className='country-address-input'>
                <div className='radio-country-input'>
                    <label>
                        <input
                            // ref={this._CountryPickerRadioRef}
                            type='radio'
                            onChange={this._handleChange('country')}
                            aria-label='NZ'
                            checked={this.state.country === 'NZ'}
                            value={'NZ'}
                            name={'NZ'}
                            className='nz-input-text'
                        />
                        NZ</label>
                </div>
                <div className='radio-country-input'>
                    <label>
                        <input
                            // ref={this._CountryPickerRadioRef}
                            type='radio'
                            onChange={this._handleChange('country')}
                            aria-label='AU'
                            checked={this.state.country === 'AU'}
                            value={'AU'}
                            name={'AU'}
                            className='au-input-text'
                        />
                        AU</label>
                </div>
            </div>
        );
       
    }

    private _renderAlert(className: string): JSX.Element {
        return <></>;
    }

    private _renderCheckboxTermsAndConditions(): React.ReactNode {
        return (
            <div className='checkbox-privacy-policy'>
                {this.state.tsCsError ? this._renderAlert('checkbox-privacy-policy') : ''}
                <div className='checkbox-privacy-policy-content'>
                    <input
                        ref={this._TermsAndContitionsRef}
                        type='checkbox'
                        className='privacy-policy-text'
                        aria-label='Accept Terms and Conditions'
                        aria-checked='false'
                        onChange={this._handleChange('termsAndConditions')}
                        id='termsAndConditionsCheckbox'
                    />
                    <span className='checkbox-privacy-policy-text'>
                        <p>{this.props.config.termsAndConditionsText}<a href={this.props.config.termsAndConditionsLink}>Terms and Conditions</a></p>
                    </span>
                </div>
            </div>
        );
    }

    private _renderCheckboxPrivacyPolicy(): React.ReactNode {
        return (
            <div className='checkbox-privacy-policy'>
                {this.state.tsCsError ? this._renderAlert('checkbox-privacy-policy') : ''}
                <div className='checkbox-privacy-policy-content'>
                    <input
                        ref={this._PrivacyPolicyRef}
                        type='checkbox'
                        className='privacy-policy-text'
                        aria-label='Accept Privacy Policy'
                        aria-checked='false'
                        onChange={this._handleChange('privacyPolicy')}
                        id='privacyPolicyCheckbox'
                    />
                    <span className='checkbox-privacy-policy-text'>
                        <p>{this.props.config.privacyPolicyText}<a href={this.props.config.privacyPolicyLink}>Privacy Policy</a></p>
                    </span>
                </div>
            </div>
        );
    }

    private _renderReCaptcha(): React.ReactNode {
        //recaptcha newsletter-recapture-visable
        return (
            <div>
                {
                    this.props.config.reCaptchaOption ? <div className=''>
                        {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}

                        <ReCAPTCHA
                            ref={this._recaptchaRef}
                            sitekey={this.props.config.reCaptchaSiteKey!}
                            size="invisible"
                            onChange={() => this._submit()}
                        />
                    </div> : null
                }
            </div>
        );
    }

    
    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private _renderSubmitButton(): React.ReactNode {
        return (
            <div className="subscribe-button-container">
                <Button
                    className={`subscribe-button btn-thirdary`}
                    title={this.props.config.submitButtonLabel}
                    color='primary'
                    onClick={this._handleSubmit}
                >{this.props.config.submitButtonLabel}
                </Button>
            </div>
        );
    }

    private _renderSubmitThanks(): React.ReactNode {
        return (
            <div>
                <div className={`subscribe-thanks`}>
                    Thanks!
                </div>
            </div>
        );
    }

    private _showErrors = (): void => {
        this.setState({
            emailErrorClass: this._checkEmailError() ? 'show' : 'hide',
            firstNameErrorClass: (this._FirstNameInputRef.current && this._FirstNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            lastNameErrorClass: (this._LastNameInputRef.current && this._LastNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            tsCsErrorClass: (this._TermsAndContitionsRef.current && !this._TermsAndContitionsRef.current.checked) ? 'show' : 'hide'
        });
    }

    private _checkEmailError = (): boolean => {
        const email: string = this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value || '';
        if (email.length === 0) {
            return true;
        } else {
            return !this._isEmailValid(email);
        }
    }

    private _renderErrors = (): JSX.Element => {
        const {resources} = this.props;
        return (
            <>
                <div className={this.state.firstNameErrorClass}>
                    {resources.firstNameError}
                </div>
                <div className={this.state.lastNameErrorClass}>
                    {resources.lastNameError}
                </div>
                <div className={this.state.emailErrorClass}>
                    {resources.emailError}
                </div>
                <div className={this.state.tsCsErrorClass}>
                    {resources.tsCsError}
                </div>
                
            </>
        );
    }

    private _handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        switch (name) {
            case 'firstName': {
                this.setState({ firstName: value, fNError: false, reqError: false, eAValError: false, tsCsError: false });
                break;
            }
            case 'lastName': {
                this.setState({ lastName: value, lNError: false, reqError: false, eAValError: false, tsCsError: false });
                break;
            }
            case 'emailAddress': {
                this.setState({ emailAddress: value, eAError: false, reqError: false, eAValError: false, tsCsError: false });
                break;
            }
            case 'country': {
                this.setState({ country: value, eAError: false, reqError: false, eAValError: false, tsCsError: false });
                break;
            }
            case 'termsAndConditions': {
                const isChecked = this._TermsAndContitionsRef.current && this._TermsAndContitionsRef.current.checked ? this._TermsAndContitionsRef.current.checked : false;
                const recaptchaWrapper = document.querySelector('.recaptcha');
                recaptchaWrapper && recaptchaWrapper.classList.toggle('newsletter-recapture-visable', !isChecked);
                this.setState({ termsAndContitionsChecked: isChecked, reqError: false, eAValError: false, tsCsError: false });
                break;
            }
            case 'privacyPolicy': {
                const isChecked = this._PrivacyPolicyRef.current && this._PrivacyPolicyRef.current.checked ? this._PrivacyPolicyRef.current.checked : false;
                this.setState({ privacyPolicyChecked: isChecked, reqError: false, eAValError: false, tsCsError: false });
                break;
            }
            default: {
                console.log('No input box changes');
                this.setState({ reqError: false, eAValError: false, tsCsError: false });
            }
        }
    }

    private _isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private async _handleSubmit(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): Promise<void> {
        await this._recaptchaRef.current?.execute()
    }

    private async _submit(): Promise<void> {
        this._showErrors();
        
        this.firstNameValue = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? true : false;
        this.lastNameValue = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? true : false;
        this.emailAddressValue = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? true : false;        

        const fN = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? this._FirstNameInputRef.current.value : '';
        const lN = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? this._LastNameInputRef.current.value : '';
        const eA = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? this._EmailAddressInputRef.current.value : '';
        const cT = this.state.country;

        const eAVal = this._EmailAddressInputRef.current!.value;
        const oI = this._PrivacyPolicyRef.current!.checked;
        const recaptchaValue = this._recaptchaRef.current?.getValue();

        if (!this.firstNameValue) {
            this.setState({
                fNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this.lastNameValue) {
            this.setState({
                lNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
         } else if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this._isEmailValid(eAVal)) {
            this.setState({
                eAValError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!recaptchaValue) {
            this.setState({
                rCCError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this._TermsAndContitionsRef.current!.checked) {
            this.setState({
                termsAndContitionsChecked: false,
                tsCsError: true
            });
            this._recaptchaRef.current?.reset();
        } else {
            this.formInputValues = (this.emailAddressValue) ? true : false;
            if (this.formInputValues && this.state.termsAndContitionsChecked && !this.state.eAValError) {
                try {
                    await this._postEntry(fN, lN, eA, oI, cT || 'NZ');

                    this._clearValues();
                    this._recaptchaRef.current?.reset();
                    
                } catch (e) {
                    console.log(e, 'Please fill in the form!');
                }
            }
        }
    }

    private async _postEntry(firstName: string, lastName: string, email: string, optIn: boolean, country: string): Promise<void> {
        // NB in test the post body will need to look like this: { content: { emailAddress: email } }
        /* tslint:disable */

        let emarsysGroupId = this.props.config.emarsysGroupId, 
        emarsysCompetitionGroupId = this.props.config.emarsysCompetitionGroupId,
        emarsysGroupName = this.props.config.emarsysGroupOptInName;

        if (country === 'AU') {
            emarsysGroupId = this.props.config.emarsysGroupIdAU; 
            emarsysCompetitionGroupId = this.props.config.emarsysCompetitionGroupIdAU;
        }
       
        const response = await MsDyn365.commerceApiRequest(
            this.props.context.request,
            'PostJournalCompetitionForm',
            'post',
            {
                firstName: firstName,
                lastName: lastName,
                emailAddress: email,
                country: country,
                emarsysGroupId: emarsysGroupId,
                emarsysCompetitionGroupId: emarsysCompetitionGroupId,
                emarsysGroupName: emarsysGroupName,
                optIn: optIn
            }
            
        ) as any;
        
        this.responseMessage = response?.data.Message;

        if (response?.data.Result !== 'Error') {

            if (MsDyn365.msdyn365Commerce.isBrowser && this.props.config.urlToOpenOnSuccess) {
                // @ts-ignore
                window.location = this.props.config.urlToOpenOnSuccess;
            }

            this.setState({
                responseReceived: true,
                responseError: response?.data.Result === 'Error',
                responseSuccess: response?.data.Result !== 'Error',
                showResponseSuccess: true,
                emailAddress: '',
                firstName: '',
                lastName: '',
                termsAndContitionsChecked: false,
                reqError: false,
                eAValError: false,
                rCCError: false
            });
        } else {
            this.setState({
                responseReceived: true,
                responseError: response?.data.Result === 'Error',
                responseSuccess: response?.data.Result !== 'Error',
                emailAddress: '',
                termsAndContitionsChecked: false,
                reqError: false,
                eAValError: false,
                rCCError: false
            });
        }

        // if (response?.data.Result !== 'Error') {
        //     // const baseUrl = window.location.origin;
        //     // if (baseUrl.length) {
        //     //     // window.location.assign(`${baseUrl}${this.props.config.successUrl}`);
        //     // }
        // } else {
        //     this.setState({
        //         responseReceived: true,
        //         responseError: response?.data.Result === 'Error',
        //         responseSuccess: response?.data.Result !== 'Error',
        //         emailAddress: '',
        //         termsAndContitionsChecked: false,
        //         reqError: false,
        //         eAValError: false,
        //         rCCError: false
        //     });
        // }
        /* tslint:enable */
    }

    private _clearValues(): void {
        if (this._TermsAndContitionsRef.current) {
            this._TermsAndContitionsRef.current.checked = false;
            const recaptchaWrapper = document.querySelector('.recaptcha');
            recaptchaWrapper && recaptchaWrapper.classList.toggle('newsletter-recapture-visable', true);
        }
        if (this._PrivacyPolicyRef.current) {
            this._PrivacyPolicyRef.current.checked = false;
        }
        if (this._EmailAddressInputRef.current) {
            this._EmailAddressInputRef.current!.value = '';
        }
        if (this._FirstNameInputRef.current) {
            this._FirstNameInputRef.current!.value = '';
        }
        if (this._LastNameInputRef.current) {
            this._LastNameInputRef.current!.value = '';
        }
    }

}

export default CittaCompetitionForm;


